import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  RequiredIndicator,
  useToast,
} from '@chakra-ui/react';
import { default as NextLink } from 'next/link';
import React from 'react';
import { useForm } from 'react-hook-form';
import {
  CreateOrderInput,
  Mutation,
  MutationCreateOrderArgs,
  ParagraphFormular,
} from '../../generated/types';
import { EmailIcon, PhoneIcon } from '@chakra-ui/icons';
import { useMutation } from '@apollo/client';
import { CREATE_ORDER_QUERY } from '../../queries';
import { useStore } from '../../store/provider';

interface EbikeAtlasOrderFormValues {
  company: string;
  firstname: string;
  lastname: string;
  street: string;
  postcode: string;
  city: string;
  email: string;
  phone: string;
}

interface EbikeAtlasOrderFormProps {
  data: ParagraphFormular;
}

export default function EbikeAtlasOrderForm({
  data,
}: EbikeAtlasOrderFormProps) {
  // const toast = useToast();
  const [createOrder, { loading }] = useMutation<
    Mutation,
    MutationCreateOrderArgs
  >(CREATE_ORDER_QUERY);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
  } = useForm<EbikeAtlasOrderFormValues>();
  const store = useStore();
  const toast = useToast();

  const onSubmit = async (values: EbikeAtlasOrderFormValues) => {
    const apiValues: CreateOrderInput = {
      company: values.company,
      firstname: values.firstname,
      lastname: values.lastname,
      street: values.street,
      postcode: values.postcode,
      city: values.city,
      email: values.email,
      phone: values.phone,
      formular: data.formular,
      frontend: store.config.frontendName,
    };

    const result = await createOrder({
      variables: {
        data: apiValues,
      },
    });

    if (result.data) {
      if (result.data.createOrder && result.data.createOrder.created) {
        toast({
          title: 'Erfolg',
          description: data.textConfirm,
          status: 'success',
          duration: 6000,
          isClosable: true,
        });

        reset();
        return;
      } else {
        if (result.data.createOrder?.errors) {
          result.data.createOrder?.errors.forEach((error) => {
            setError(error.field as any, error);
          });
        }
      }
    }

    toast({
      title: 'Fehler',
      description: data.textError,
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl flex="1" isRequired>
        <FormLabel htmlFor="company">Firma</FormLabel>
        <Input
          variant="outline"
          type="text"
          id="company"
          name="company"
          {...register('company', {
            required: true,
          })}
        />
      </FormControl>
      <Flex marginTop="4">
        <FormControl flex="1" isRequired>
          <FormLabel htmlFor="firstname">Vorname</FormLabel>
          <Input
            variant="outline"
            type="text"
            id="firstname"
            name="firstname"
            {...register('firstname', {
              required: true,
            })}
          />
        </FormControl>
        <FormControl flex="1" isRequired marginLeft="2">
          <FormLabel htmlFor="lastname">Nachname</FormLabel>
          <Input
            type="text"
            id="lastname"
            name="lastname"
            {...register('lastname', {
              required: true,
            })}
          />
        </FormControl>
      </Flex>

      <FormControl marginTop="4" isRequired>
        <FormLabel htmlFor="street">Straße</FormLabel>
        <Input
          name="street"
          type="text"
          id="street"
          {...register('street', {
            required: true,
          })}
        />
      </FormControl>

      <Flex marginTop="4">
        <FormControl flex="1" isRequired>
          <FormLabel htmlFor="postcode">PLZ</FormLabel>
          <Input
            name="postcode"
            type="text"
            id="postcode"
            {...register('postcode', {
              required: true,
            })}
          />
        </FormControl>

        <FormControl flex="1" marginLeft="2" isRequired>
          <FormLabel htmlFor="city">Ort</FormLabel>
          <Input
            name="city"
            type="text"
            id="city"
            {...register('city', {
              required: true,
            })}
          />
        </FormControl>
      </Flex>

      <Flex marginTop="4">
        <FormControl flex="1" isRequired>
          <FormLabel htmlFor="email">E-Mail</FormLabel>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<EmailIcon color="gray.300" />}
            />
            <Input
              type="email"
              id="email"
              name="email"
              {...register('email', {
                required: true,
              })}
            />
          </InputGroup>
        </FormControl>

        <FormControl flex="1" marginLeft="2">
          <FormLabel htmlFor="phone">Telefon</FormLabel>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<PhoneIcon color="gray.300" />}
            />
            <Input type="text" id="phone" name="phone" {...register('phone')} />
          </InputGroup>
        </FormControl>
      </Flex>

      <FormControl marginTop={6} isRequired flex="1">
        <Checkbox isRequired>
          Ich bin damit einverstanden, dass meine Daten zur Kontaktaufnahme
          gespeichert werden.
          <RequiredIndicator />
        </Checkbox>
      </FormControl>

      <FormControl marginTop={2} isRequired flex="1">
        <Checkbox isRequired>
          Ich habe die{' '}
          <Link textDecoration="underline" as="span">
            <NextLink href="/agb">Datenschutzbestimmungen</NextLink>
          </Link>{' '}
          gelesen und bestätige diese.
          <RequiredIndicator />
        </Checkbox>
      </FormControl>

      <Flex marginTop={8} justifyContent="flex-end">
        <Button
          disabled={loading}
          isLoading={loading}
          width="100%"
          variant="outline"
          type="submit"
          colorScheme="blue"
        >
          Abschicken
        </Button>
      </Flex>
    </form>
  );
}
